import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Coins, AlertTriangle, CheckCircle, Info, 
  Calendar, Clock, Shield, Calculator
} from 'lucide-react';
import api from '../../lib/api';

interface RedeemableToken {
  tokenId: string;
  name: string;
  symbol: string;
  quantity: number;
  redeemableQuantity: number;
  lockedQuantity: number;
  lockUpEndDate: string | null;
  unitPrice: number;
  navPrice: number;
  redemptionFee: number;
  minRedemption: number;
  redemptionWindowOpen: boolean;
  nextRedemptionWindow: string | null;
  maturityDate: string | null;
}

interface RedemptionRequest {
  id: string;
  tokenId: string;
  tokenName: string;
  tokenSymbol: string;
  quantity: number;
  navPrice: number;
  grossValue: number;
  fee: number;
  netValue: number;
  status: 'PENDING' | 'APPROVED' | 'PROCESSING' | 'COMPLETED' | 'REJECTED';
  requestedAt: string;
  estimatedCompletionDate: string | null;
}

export function RedeemToken() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const { data: tokens, isLoading: tokensLoading } = useQuery({
    queryKey: ['redeemable-tokens'],
    queryFn: async () => {
      const res = await api.get('/tokenization/portfolio/redeemable');
      return res.data as RedeemableToken[];
    },
  });

  const { data: pendingRequests } = useQuery({
    queryKey: ['redemption-requests'],
    queryFn: async () => {
      const res = await api.get('/tokenization/redemptions/pending');
      return res.data as RedemptionRequest[];
    },
  });

  const redeemMutation = useMutation({
    mutationFn: async (data: { tokenId: string; quantity: number }) => {
      const res = await api.post('/tokenization/redemptions', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redeemable-tokens'] });
      queryClient.invalidateQueries({ queryKey: ['redemption-requests'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      setSelectedToken(null);
      setQuantity('');
      setAgreedToTerms(false);
    },
  });

  const selectedTokenData = tokens?.find(t => t.tokenId === selectedToken);
  const redeemQuantity = parseInt(quantity) || 0;
  const grossValue = redeemQuantity * (selectedTokenData?.navPrice || 0);
  const fee = Math.round(grossValue * (selectedTokenData?.redemptionFee || 0) / 100);
  const netValue = grossValue - fee;

  const canRedeem = 
    selectedToken &&
    redeemQuantity > 0 &&
    redeemQuantity <= (selectedTokenData?.redeemableQuantity || 0) &&
    redeemQuantity >= (selectedTokenData?.minRedemption || 1) &&
    selectedTokenData?.redemptionWindowOpen &&
    agreedToTerms;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/banking/tokens/portfolio')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Resgatar Tokens</h1>
          <p className="text-surface-400">Solicite o resgate dos seus tokens</p>
        </div>
      </div>

      {/* Pending Requests */}
      {pendingRequests && pendingRequests.length > 0 && (
        <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Solicitações Pendentes
          </h3>
          <div className="space-y-2">
            {pendingRequests.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
                <div>
                  <p className="font-medium">{request.tokenName} ({request.tokenSymbol})</p>
                  <p className="text-sm text-surface-400">
                    {request.quantity.toLocaleString('pt-BR')} tokens • Solicitado em {formatDate(request.requestedAt)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(request.netValue)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    request.status === 'COMPLETED' ? 'bg-green-500/20 text-green-400' :
                    request.status === 'REJECTED' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {request.status === 'PENDING' ? 'Pendente' :
                     request.status === 'APPROVED' ? 'Aprovado' :
                     request.status === 'PROCESSING' ? 'Processando' :
                     request.status === 'COMPLETED' ? 'Concluído' : 'Rejeitado'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Token Selection */}
        <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
          <h3 className="font-semibold mb-4">Selecione o Token</h3>
          
          {tokensLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !tokens?.length ? (
            <div className="text-center py-8">
              <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <p className="text-surface-400">Nenhum token disponível para resgate</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tokens.map((token) => (
                <button
                  key={token.tokenId}
                  onClick={() => {
                    setSelectedToken(token.tokenId);
                    setQuantity('');
                  }}
                  disabled={!token.redemptionWindowOpen}
                  className={`w-full p-4 rounded-lg text-left transition-colors ${
                    selectedToken === token.tokenId
                      ? 'bg-primary-600/20 border border-primary-500'
                      : token.redemptionWindowOpen
                      ? 'bg-surface-800 hover:bg-surface-700 border border-surface-700'
                      : 'bg-surface-800/50 border border-surface-700 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-primary-400" />
                      <span className="font-medium">{token.name}</span>
                      <span className="text-sm text-surface-400">{token.symbol}</span>
                    </div>
                    {!token.redemptionWindowOpen && (
                      <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">
                        Janela Fechada
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-surface-400">Disponível: </span>
                      <span>{token.redeemableQuantity.toLocaleString('pt-BR')}</span>
                    </div>
                    <div>
                      <span className="text-surface-400">NAV: </span>
                      <span>{formatCurrency(token.navPrice)}</span>
                    </div>
                  </div>
                  {token.lockedQuantity > 0 && (
                    <p className="text-xs text-yellow-400 mt-2">
                      {token.lockedQuantity} tokens em lock-up até {formatDate(token.lockUpEndDate!)}
                    </p>
                  )}
                  {!token.redemptionWindowOpen && token.nextRedemptionWindow && (
                    <p className="text-xs text-surface-400 mt-2">
                      Próxima janela: {formatDate(token.nextRedemptionWindow)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Redemption Form */}
        <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
          <h3 className="font-semibold mb-4">Detalhes do Resgate</h3>

          {!selectedToken ? (
            <div className="text-center py-8">
              <Calculator className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <p className="text-surface-400">Selecione um token para continuar</p>
            </div>
          ) : selectedTokenData && (
            <div className="space-y-6">
              {/* Quantity Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Quantidade a Resgatar</label>
                  <span className="text-sm text-surface-400">
                    Disponível: {selectedTokenData.redeemableQuantity.toLocaleString('pt-BR')}
                  </span>
                </div>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  min={selectedTokenData.minRedemption}
                  max={selectedTokenData.redeemableQuantity}
                  className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-lg"
                />
                <div className="flex gap-2 mt-2">
                  {[25, 50, 75, 100].map((percent) => (
                    <button
                      key={percent}
                      onClick={() => setQuantity(Math.floor(selectedTokenData.redeemableQuantity * percent / 100).toString())}
                      className="flex-1 py-1 text-sm bg-surface-800 hover:bg-surface-700 rounded transition-colors"
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-surface-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-surface-400">Quantidade</span>
                  <span>{redeemQuantity.toLocaleString('pt-BR')} {selectedTokenData.symbol}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">NAV por token</span>
                  <span>{formatCurrency(selectedTokenData.navPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor bruto</span>
                  <span>{formatCurrency(grossValue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Taxa de resgate ({selectedTokenData.redemptionFee}%)</span>
                  <span className="text-red-400">-{formatCurrency(fee)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-surface-700 font-semibold">
                  <span>Valor líquido</span>
                  <span className="text-green-400">{formatCurrency(netValue)}</span>
                </div>
              </div>

              {/* Warnings */}
              {redeemQuantity > 0 && redeemQuantity < selectedTokenData.minRedemption && (
                <div className="flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">
                    Resgate mínimo: {selectedTokenData.minRedemption.toLocaleString('pt-BR')} tokens
                  </span>
                </div>
              )}

              {/* Info */}
              <div className="flex items-start gap-2 p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg text-blue-400">
                <Info className="w-4 h-4 mt-0.5" />
                <span className="text-sm">
                  O resgate será processado em até 5 dias úteis. O valor será creditado na sua conta virtual.
                </span>
              </div>

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-surface-400">
                  Confirmo que desejo resgatar os tokens selecionados e entendo que a operação é irreversível após aprovação.
                </span>
              </label>

              {/* Submit */}
              <button
                onClick={() => redeemMutation.mutate({
                  tokenId: selectedToken,
                  quantity: redeemQuantity,
                })}
                disabled={!canRedeem || redeemMutation.isPending}
                className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors font-semibold"
              >
                {redeemMutation.isPending ? 'Processando...' : 'Solicitar Resgate'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RedeemToken;
